import { useEffect } from 'react';
import Head from 'next/head';
import SEO from '@seo/index';
import { useCommonData } from '@stores/AppCommonData';
import { UpscSeoPageConfig } from '@cont/UpscCurrentAffairs/upscSeoTypes';
import { getUpscCanonicalUrl, resolveUpscStructuredData } from './upscSeoUtils';

export interface UpscCurrentAffairsSeoProps {
  pageSeo?: UpscSeoPageConfig | null;
  canonicalPath?: string;
}

const UpscCurrentAffairsSeo = ({
  pageSeo,
  canonicalPath = ''
}: UpscCurrentAffairsSeoProps) => {
  const setPageSEOContent = useCommonData((state) => state.setPageSEOContent);
  const metaTitle = pageSeo?.metaTitle ?? '';
  const metaDescription = pageSeo?.metaDescription ?? '';
  const robotsMeta = pageSeo?.robotsMeta ?? 'index, follow';
  const canonicalUrl = getUpscCanonicalUrl(canonicalPath);
  const structuredData = resolveUpscStructuredData(pageSeo?.structuredData);
  const hasSeoContent = Boolean(metaTitle || metaDescription || structuredData);

  useEffect(() => {
    if (!hasSeoContent) {
      return undefined;
    }

    const syncTimer = window.setTimeout(() => {
      setPageSEOContent({
        title: metaTitle,
        description: metaDescription
      });
    }, 0);

    return () => {
      window.clearTimeout(syncTimer);
      setPageSEOContent({ title: '', description: '' });
    };
  }, [hasSeoContent, metaTitle, metaDescription, setPageSEOContent]);

  if (!pageSeo || !hasSeoContent) {
    return null;
  }

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        url={canonicalUrl}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          url: canonicalUrl
        }}
        schema={structuredData}
      />
      <Head>
        {metaTitle ? <title key="title">{metaTitle}</title> : null}
        {robotsMeta ? (
          <meta key="robots" name="robots" content={robotsMeta} />
        ) : null}
      </Head>
    </>
  );
};

export default UpscCurrentAffairsSeo;
